<script lang="ts">
  // The in-world menu overlay, extracted out of World.svelte so it's a single component the duel/world
  // shares AND the Texture Pack editor can render in isolation. Themed via --worldmenu-* (each falling
  // back to the normal look), so a pack can re-skin the overworld menu on its own.
  import type { ComponentProps } from "svelte";
  import { fade, scale } from "svelte/transition";
  import { expoOut } from "svelte/easing";
  import DataWindow from "./ui/DataWindow.svelte";
  import PortraitPreview from "./PortraitPreview.svelte";
  import RankBadge from "./RankBadge.svelte";
  import { draggableDialog } from "../actions/draggableDialog.ts";

  interface Item { icon: string; label: string; desc: string; action: () => void }
  let { items, name, dp = 0, look = null, rank = null, onClose, onLogout }: {
    items: Item[];
    name: string;
    dp?: number;
    look?: ComponentProps<typeof PortraitPreview>["look"] | null;
    rank?: ComponentProps<typeof RankBadge>["rank"] | null;
    onClose: () => void;
    onLogout: () => void;
  } = $props();
</script>

{#snippet ic(n: string, cls: string)}
  <svg viewBox="0 0 24 24" class={cls} fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    {#if n === "swords"}
      <polyline points="14.5 17.5 3 6 3 3 6 3 17.5 14.5" /><line x1="13" x2="19" y1="19" y2="13" /><line x1="16" x2="20" y1="16" y2="20" /><line x1="19" x2="21" y1="21" y2="19" /><polyline points="14.5 6.5 18 3 21 3 21 6 17.5 9.5" /><line x1="5" x2="9" y1="14" y2="18" /><line x1="7" x2="4" y1="17" y2="20" /><line x1="3" x2="5" y1="19" y2="21" />
    {:else if n === "trophy"}
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" /><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" /><path d="M4 22h16" /><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" /><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" /><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
    {:else if n === "ranks"}
      <line x1="4" x2="4" y1="21" y2="11" /><line x1="12" x2="12" y1="21" y2="4" /><line x1="20" x2="20" y1="21" y2="14" /><line x1="2.5" x2="21.5" y1="21" y2="21" />
    {:else if n === "deck"}
      <path d="M12.8 2.2a2 2 0 0 0-1.6 0L2.6 6.1a1 1 0 0 0 0 1.8l8.6 3.9a2 2 0 0 0 1.6 0l8.6-3.9a1 1 0 0 0 0-1.8Z" /><path d="m22 17.7-9.2 4.1a2 2 0 0 1-1.6 0L2 17.7" /><path d="m22 12.7-9.2 4.1a2 2 0 0 1-1.6 0L2 12.7" />
    {:else if n === "edit"}
      <path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
    {:else if n === "gear"}
      <circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
    {:else if n === "arrow"}
      <path d="M5 12h14" /><path d="m13 6 6 6-6 6" />
    {:else if n === "close"}
      <path d="M18 6 6 18" /><path d="m6 6 12 12" />
    {:else if n === "logout"}
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="m16 17 5-5-5-5" /><path d="M21 12H9" />
    {:else if n === "replay"}
      <circle cx="12" cy="12" r="9" /><path d="m10 8.5 5.5 3.5-5.5 3.5Z" />
    {/if}
  </svg>
{/snippet}

<div class="mobile-dialog-host fixed inset-0 z-40 grid place-items-center">
  <button
    class="tp-worldmenu-scrim modal-scrim absolute inset-0 bg-void/50"
    transition:fade={{ duration: 160 }}
    onclick={onClose}
    aria-label="Close menu"
  ></button>

  <div
    class="mobile-full-dialog relative w-[min(380px,92vw)]"
    role="dialog"
    aria-modal="true"
    aria-label="World menu"
    use:draggableDialog
    transition:scale={{ start: 0.96, opacity: 0, duration: 220, easing: expoOut }}
  >
    <DataWindow c={18} fill class="tp-worldmenu mobile-dialog-panel">
      <!-- header -->
      <div data-dialog-drag-handle class="tp-worldmenu-header dialog-drag-handle flex items-center justify-between border-b border-cyan-dim/40 bg-surface-2/70 px-5 py-2.5">
        <span class="flex items-center gap-2">
          <span class="size-1.5 rounded-full bg-cyan animate-pulse-soft" style="filter:drop-shadow(0 0 5px var(--color-cyan))"></span>
          <span class="font-display text-[0.72rem] font-semibold uppercase tracking-[0.26em] text-cyan">Menu</span>
        </span>
        <button onclick={onClose} class="text-ink-dim transition-colors hover:text-cyan" aria-label="Close menu">{@render ic("close", "size-4")}</button>
      </div>

      <!-- identity strip -->
      <div class="worldmenu-identity flex items-center gap-3 border-b border-cyan-dim/15 px-5 py-3">
        <div class="grid size-10 place-items-center overflow-hidden rounded-sm bg-cyan/12">
          {#if look}<PortraitPreview size={40} {look} headshot />{:else}<span class="font-display text-sm font-bold text-cyan">{name.slice(0, 1).toUpperCase()}</span>{/if}
        </div>
        <div class="leading-tight">
          <div class="font-display text-sm font-bold tracking-wide text-ink">{name}</div>
          {#if rank}<div class="-mt-0.5 mb-0.5"><RankBadge {rank} size="sm" /></div>{/if}
          <div class="flex items-baseline gap-1">
            <span class="text-[10px] uppercase tracking-widest text-ink-dim">DP</span>
            <span class="font-display text-sm font-bold text-gold">{dp.toLocaleString()}</span>
          </div>
        </div>
      </div>

      <!-- actions: each row charges up on hover (edge lights, sweep, icon pops) -->
      <div class="worldmenu-actions mobile-dialog-scroll space-y-2 overflow-y-auto scroll-cyber p-3">
        {#each items as it (it.label)}
          <button onclick={it.action} class="tp-worldmenu-item group block w-full chamfer fx-lift bg-cyan-dim/25 p-px text-left hover:bg-cyan/60 hover:glow-cyan" style="--c:10px">
            <span class="chamfer fx-sweep flex items-center gap-3 bg-void-2/85 px-3.5 py-3 transition-colors duration-200 group-hover:bg-surface-2/85" style="--c:9px">
              <span class="tp-worldmenu-icon grid size-9 shrink-0 place-items-center chamfer-btn bg-cyan/12 text-cyan transition-[transform,background-color] duration-200 group-hover:scale-110 group-hover:bg-cyan/22" style="--c:6px">{@render ic(it.icon, "size-5")}</span>
              <span class="min-w-0 flex-1">
                <span class="block font-display text-sm font-semibold tracking-wide text-ink transition-colors duration-200 group-hover:text-cyan">{it.label}</span>
                <span class="block text-xs text-ink-dim">{it.desc}</span>
              </span>
              <span class="shrink-0 text-cyan/60 transition-[transform,color] duration-200 group-hover:translate-x-1 group-hover:text-cyan">{@render ic("arrow", "size-4")}</span>
            </span>
          </button>
        {/each}
      </div>

      <!-- logout -->
      <div class="space-y-2 border-t border-cyan-dim/25 p-3">
        <button onclick={onLogout} class="chamfer-btn fx-lift fx-sweep flex w-full items-center justify-center gap-2 bg-void-2/40 py-2.5 font-display text-sm font-semibold uppercase tracking-[0.12em] text-ink-dim hover:bg-magenta/10 hover:text-magenta" style="--c:8px">{@render ic("logout", "size-4")} Log Out</button>
      </div>
    </DataWindow>
  </div>
</div>

<style>
  :global(.phone) .worldmenu-identity {
    display: none;
  }
  :global(.phone) .worldmenu-actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    align-content: start;
    gap: 0.45rem;
    padding: 0.6rem;
  }
  :global(.phone) .worldmenu-actions > button > span {
    min-height: 52px;
    padding: 0.4rem 0.7rem;
  }
  :global(.phone) .worldmenu-actions .tp-worldmenu-icon {
    width: 2rem;
    height: 2rem;
  }
</style>
